// Custom date parsers that handle different precision levels

/**
 * Parse date string with seconds precision
 * @param {string} dateString - Date string in format: 2025-07-29T09:17:49
 * @returns {Date} Parsed date object
 */
export function parseDateWithSeconds(dateString) {
  if (!dateString) return new Date();

  // Handle format: 2025-07-29T09:17:49
  const dateWithSeconds = /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})/.exec(dateString);

  if (dateWithSeconds) {
    const [, datePart] = dateWithSeconds;
    const isoString = `${datePart}Z`;
    return new Date(isoString);
  }

  // Fallback to standard parsing
  return new Date(dateString);
}

/**
 * Parse date string with milliseconds precision
 * @param {string} dateString - Date string in format: 2025-07-29T09:17:49.295
 * @returns {Date} Parsed date object
 */
export function parseDateWithMilliseconds(dateString) {
  if (!dateString) return new Date();

  // Handle format: 2025-07-29T09:17:49.295
  const dateWithMilliseconds = /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})\.(\d{3})/.exec(dateString);

  if (dateWithMilliseconds) {
    const [, datePart, milliseconds] = dateWithMilliseconds;
    const isoString = `${datePart}.${milliseconds}Z`;
    return new Date(isoString);
  }

  // Fallback to seconds precision
  return parseDateWithSeconds(dateString);
}

/**
 * Parse date string with microseconds precision
 * @param {string} dateString - Date string in format: 2025-07-29T09:17:49.295602
 * @returns {Date} Parsed date object with additional microseconds properties
 */
export function parseDateWithMicroseconds(dateString) {
  if (!dateString) return new Date();

  // Handle format: 2025-07-29T09:17:49.295602
  // JavaScript Date can only handle milliseconds, so we preserve microseconds info but truncate for Date object
  const dateWithMicroseconds = /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})\.(\d{6})/.exec(dateString);

  if (dateWithMicroseconds) {
    const [, datePart, microseconds] = dateWithMicroseconds;
    const milliseconds = microseconds.substring(0, 3); // Take only first 3 digits for Date object
    const isoString = `${datePart}.${milliseconds}Z`;
    const parsedDate = new Date(isoString);

    // Store full microseconds precision as a custom property for potential future use
    parsedDate._microseconds = microseconds;
    parsedDate._originalString = dateString;

    return parsedDate;
  }

  // Fallback to milliseconds precision
  return parseDateWithMilliseconds(dateString);
}

/**
 * Main parsing function that automatically detects precision
 * @param {string} dateString - ISO date string with varying precision
 * @returns {Date} Parsed date object
 */
export function parseISODateString(dateString) {
  if (!dateString) return new Date();

  // Auto-detect precision based on decimal places
  if (dateString.includes('.')) {
    const decimalPart = dateString.split('.')[1];
    if (decimalPart) {
      const digits = decimalPart.replace(/[^0-9]/g, ''); // Remove non-digits (like Z)

      if (digits.length >= 6) {
        return parseDateWithMicroseconds(dateString);
      } else if (digits.length >= 3) {
        return parseDateWithMilliseconds(dateString);
      }
    }
  }

  return parseDateWithSeconds(dateString);
}

/**
 * Legacy alias for backward compatibility
 * @deprecated Use parseISODateString instead
 */
export function parseJSON(dateString) {
  return parseISODateString(dateString);
}
